import { ApolloError, useMutation, useQuery } from '@apollo/client';
import {
  CalendarCheck,
  CalendarClock,
  CalendarX,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  DoorOpen,
  Hourglass,
  MoreHorizontal,
  Plus,
  Stethoscope,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  APPOINTMENTS_QUERY,
  APPT_CONSULTING_ROOMS_QUERY,
  BOOK_APPOINTMENT_MUTATION,
  CANCEL_APPOINTMENT_MUTATION,
  CHECKIN_APPOINTMENT_MUTATION,
  COMPLETE_APPOINTMENT_MUTATION,
  CONFIRM_APPOINTMENT_MUTATION,
  NOSHOW_APPOINTMENT_MUTATION,
  RESCHEDULE_APPOINTMENT_MUTATION,
  START_APPOINTMENT_MUTATION,
  VETERINARIAN_USERS_QUERY,
  idempotencyContext,
} from './appointments.gql';

import { useBranchScope } from '@/modules/auth/useAuth';
import { CustomerPetPicker } from '@/modules/doctor-pos/CustomerPetPicker';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/shared/ui/card';
import { ConfirmDialog } from '@/shared/ui/confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Modal, ModalTitle } from '@/shared/ui/modal';
import { showToast } from '@/shared/ui/toast';
import { UserAvatar } from '@/shared/ui/user-avatar';


/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-return */

const KINDS = ['consultation', 'vaccination', 'follow_up', 'surgery', 'grooming', 'emergency', 'other'] as const;
const STATUSES = ['requested', 'booked', 'confirmed', 'checked_in', 'in_progress', 'completed', 'no_show', 'cancelled', 'rescheduled'] as const;
const DURATION_PRESETS = [15, 30, 45, 60] as const;
const SELECT_CLASS = 'border-input bg-background h-10 w-full rounded-md border px-2 text-sm';

/** Left accent + badge tone per status; tinted so they read in light and dark. */
const STATUS_STYLE: Record<string, { accent: string; badge: string }> = {
  requested: { accent: 'border-l-amber-500', badge: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  booked: { accent: 'border-l-blue-500', badge: 'bg-blue-500/15 text-blue-700 dark:text-blue-300' },
  confirmed: { accent: 'border-l-emerald-500', badge: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  checked_in: { accent: 'border-l-violet-500', badge: 'bg-violet-500/15 text-violet-700 dark:text-violet-300' },
  in_progress: { accent: 'border-l-sky-500', badge: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  completed: { accent: 'border-l-border', badge: 'bg-muted text-muted-foreground' },
  no_show: { accent: 'border-l-red-500', badge: 'bg-red-500/15 text-red-700 dark:text-red-300' },
  cancelled: { accent: 'border-l-red-500', badge: 'bg-red-500/15 text-red-700 dark:text-red-300' },
  rescheduled: { accent: 'border-l-border', badge: 'bg-muted text-muted-foreground' },
};

function localDateStr(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
function todayStr(): string {
  // Local calendar date (the day window is built from local midnight; UTC date would skew
  // the "Today" view in the evening for a UTC-6 clinic).
  return localDateStr(new Date());
}
function shiftDay(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return localDateStr(d);
}
function dayWindowIso(date: string): { from: string; to: string } {
  const from = new Date(`${date}T00:00:00`);
  const to = new Date(from.getTime());
  to.setDate(to.getDate() + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}
function longDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('es-MX', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
function hm(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
/** `YYYY-MM-DDTHH:mm` in local time, the format <input type="datetime-local"> expects. */
function toLocalInput(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${localDateStr(d)}T${h}:${mi}`;
}
/** Sensible default start for a new appointment on the viewed day. */
function defaultStart(date: string): string {
  const now = new Date();
  if (date === todayStr()) {
    now.setMinutes(now.getMinutes() < 30 ? 30 : 60, 0, 0);
    return toLocalInput(now);
  }
  return `${date}T09:00`;
}
function errText(e: unknown, t: (k: string) => string): string {
  const raw = e instanceof ApolloError ? (e.graphQLErrors[0]?.message ?? e.message) : e instanceof Error ? e.message : 'error';
  if (raw.includes('appointment_overlap')) return t('appointments.error_overlap');
  if (raw.includes('consulting_room_required')) return t('appointments.error_room_required');
  return raw;
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <span className={cn('rounded-md px-2 py-0.5 text-xs font-medium', STATUS_STYLE[status]?.badge ?? 'bg-muted')}>
      {t(`appointments.status_${status}`)}
    </span>
  );
}

function ErrorBanner({ message }: { message: string }): JSX.Element {
  return (
    <div role="alert" className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
      {message}
    </div>
  );
}

function OverlapToggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- text lives in the child spans
    <label
      className={cn(
        'flex cursor-pointer items-start gap-2 rounded-md border p-2 text-xs transition-colors',
        checked ? 'border-amber-500/50 bg-amber-500/10' : 'hover:bg-muted/50',
      )}
    >
      <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <span className="block font-medium">{t('appointments.allow_overlap')}</span>
        <span className="text-muted-foreground">{t('appointments.allow_overlap_hint')}</span>
      </span>
    </label>
  );
}

function DurationPicker({ value, onChange }: { value: string; onChange: (v: string) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <Label>{t('appointments.duration_min')}</Label>
      <div className="flex flex-wrap items-center gap-1.5">
        {DURATION_PRESETS.map((m) => (
          <Button
            key={m}
            type="button"
            size="sm"
            variant={value === String(m) ? 'default' : 'outline'}
            onClick={() => onChange(String(m))}
          >
            {m} {t('appointments.min_short')}
          </Button>
        ))}
        <Input
          type="number"
          min="5"
          step="5"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-20"
          aria-label={t('appointments.duration_min')}
        />
      </div>
    </div>
  );
}

function ConfirmRequestDialog({
  appt,
  vets,
  rooms,
  onClose,
  onConfirmed,
}: {
  appt: any;
  vets: any[];
  rooms: any[];
  onClose: () => void;
  onConfirmed: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [vet, setVet] = useState<string>(appt.veterinarian_id ?? '');
  const [room, setRoom] = useState<string>(appt.consulting_room_id ?? '');
  const [error, setError] = useState<string | null>(null);
  const [confirmMutation, { loading }] = useMutation(CONFIRM_APPOINTMENT_MUTATION);
  const room_required = appt.status === 'requested';
  const can_submit = Boolean(vet) && !(room_required && !room) && !loading;

  const submit = async (): Promise<void> => {
    if (!can_submit) return;
    setError(null);
    try {
      await confirmMutation({
        variables: { appointment_id: appt.id, veterinarian_id: vet, consulting_room_id: room || undefined },
        context: idempotencyContext(),
      });
      onConfirmed();
    } catch (e) {
      setError(errText(e, t));
    }
  };

  return (
    <Modal on_close={onClose} dismissible={!loading} max_width="max-w-md">
      <Card>
        <CardHeader className="space-y-1">
          <ModalTitle>
            {t('appointments.confirm_dialog_title')} · {appt.folio_number}
          </ModalTitle>
          <p className="text-muted-foreground text-sm">
            <span className="text-foreground font-medium">{appt.pet_name ?? '—'}</span> ·{' '}
            {new Date(appt.scheduled_start_at).toLocaleDateString('es-MX', { dateStyle: 'medium' })} {hm(appt.scheduled_start_at)}
            {appt.chief_complaint ? ` · ${appt.chief_complaint}` : ''}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {error ? <ErrorBanner message={error} /> : null}
          <div className="space-y-1">
            <Label>{t('appointments.confirm_vet')}</Label>
            <select className={SELECT_CLASS} value={vet} onChange={(e) => setVet(e.target.value)}>
              <option value="">—</option>
              {vets.map((v) => <option key={v.id} value={v.id}>{v.full_name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label>{t('appointments.confirm_room')}{room_required ? ' *' : ''}</Label>
            <select className={SELECT_CLASS} value={room} onChange={(e) => setRoom(e.target.value)}>
              <option value="">{room_required ? t('appointments.confirm_room_required') : t('appointments.room_none')}</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {room_required ? <p className="text-muted-foreground text-xs">{t('appointments.confirm_room_hint')}</p> : null}
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
          <Button disabled={!can_submit} onClick={() => void submit()}>
            {loading ? t('common.loading') : t('appointments.action_confirm')}
          </Button>
        </CardFooter>
      </Card>
    </Modal>
  );
}

function BookDialog({
  rooms,
  vets,
  initialStart,
  onClose,
  onBooked,
}: {
  rooms: any[];
  vets: any[];
  initialStart: string;
  onClose: () => void;
  onBooked: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [sel, setSel] = useState<{ customer_id: string | null; pet_id: string | null }>({ customer_id: null, pet_id: null });
  const [vet, setVet] = useState('');
  const [room, setRoom] = useState('');
  const [when, setWhen] = useState(initialStart);
  const [duration, setDuration] = useState('30');
  const [kind, setKind] = useState('consultation');
  const [reason, setReason] = useState('');
  const [allow, setAllow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [book, { loading }] = useMutation(BOOK_APPOINTMENT_MUTATION);
  const can_submit = Boolean(sel.pet_id) && Boolean(vet) && when !== '' && !loading;

  const submit = async (): Promise<void> => {
    if (!can_submit || !sel.pet_id) return;
    setError(null);
    try {
      await book({
        variables: {
          input: {
            pet_id: sel.pet_id,
            veterinarian_id: vet,
            consulting_room_id: room || undefined,
            scheduled_start_at: new Date(when).toISOString(),
            duration_minutes: Number.parseInt(duration, 10) || 30,
            appointment_kind: kind,
            chief_complaint: reason.trim() || undefined,
            allow_overlap: allow || undefined,
          },
        },
        context: idempotencyContext(),
      });
      onBooked();
    } catch (e) {
      setError(errText(e, t));
    }
  };

  return (
    <Modal on_close={onClose} dismissible={!loading} max_width="max-w-xl">
      <Card>
        <CardHeader>
          <ModalTitle>{t('appointments.book_title')}</ModalTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? <ErrorBanner message={error} /> : null}

          <section className="space-y-2">
            <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{t('appointments.section_patient')}</h3>
            <CustomerPetPicker customer_id={sel.customer_id} pet_id={sel.pet_id} onChange={setSel} />
          </section>

          <section className="space-y-2">
            <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{t('appointments.section_visit')}</h3>
            <div className="space-y-1">
              <Label>{t('appointments.kind')}</Label>
              <div className="flex flex-wrap gap-1.5">
                {KINDS.map((k) => (
                  <Button key={k} type="button" size="sm" variant={kind === k ? 'default' : 'outline'} onClick={() => setKind(k)}>
                    {t(`appointments.kind_${k}`)}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label>{t('appointments.reason')}</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('appointments.reason_ph')} />
            </div>
          </section>

          <section className="space-y-2">
            <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{t('appointments.section_schedule')}</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>{t('appointments.start')}</Label>
                <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
              </div>
              <DurationPicker value={duration} onChange={setDuration} />
              <div className="space-y-1">
                <Label>{t('appointments.vet')} *</Label>
                <select className={SELECT_CLASS} value={vet} onChange={(e) => setVet(e.target.value)}>
                  <option value="">—</option>
                  {vets.map((v) => <option key={v.id} value={v.id}>{v.full_name}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>{t('appointments.room')}</Label>
                <select className={SELECT_CLASS} value={room} onChange={(e) => setRoom(e.target.value)}>
                  <option value="">{t('appointments.room_none')}</option>
                  {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            </div>
            <OverlapToggle checked={allow} onChange={setAllow} />
          </section>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
          <Button disabled={!can_submit} onClick={() => void submit()}>
            {loading ? t('common.loading') : t('appointments.book')}
          </Button>
        </CardFooter>
      </Card>
    </Modal>
  );
}

function RescheduleDialog({ appt, onClose, onDone }: { appt: any; onClose: () => void; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [when, setWhen] = useState(toLocalInput(new Date(appt.scheduled_start_at)));
  const [duration, setDuration] = useState(String(appt.duration_minutes));
  const [reason, setReason] = useState('');
  const [allow, setAllow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resched, { loading }] = useMutation(RESCHEDULE_APPOINTMENT_MUTATION);
  const unchanged = when === toLocalInput(new Date(appt.scheduled_start_at)) && duration === String(appt.duration_minutes);
  const can_submit = when !== '' && !unchanged && !loading;

  const submit = async (): Promise<void> => {
    if (!can_submit) return;
    setError(null);
    try {
      await resched({
        variables: { input: { appointment_id: appt.id, scheduled_start_at: new Date(when).toISOString(), duration_minutes: Number.parseInt(duration, 10) || appt.duration_minutes, allow_overlap: allow || undefined, reason: reason.trim() || undefined } },
        context: idempotencyContext(),
      });
      onDone();
    } catch (e) {
      setError(errText(e, t));
    }
  };

  return (
    <Modal on_close={onClose} dismissible={!loading} max_width="max-w-md">
      <Card>
        <CardHeader className="space-y-1">
          <ModalTitle>{t('appointments.reschedule_title')} · {appt.folio_number}</ModalTitle>
          <p className="text-muted-foreground text-sm">
            {t('appointments.reschedule_current')}:{' '}
            <span className="text-foreground font-medium">
              {new Date(appt.scheduled_start_at).toLocaleDateString('es-MX', { dateStyle: 'medium' })} · {hm(appt.scheduled_start_at)}–{hm(appt.scheduled_end_at)}
            </span>
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {error ? <ErrorBanner message={error} /> : null}
          <div className="space-y-1">
            <Label>{t('appointments.start')}</Label>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
          </div>
          <DurationPicker value={duration} onChange={setDuration} />
          <div className="space-y-1">
            <Label>{t('appointments.reschedule_reason')}</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <OverlapToggle checked={allow} onChange={setAllow} />
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
          <Button disabled={!can_submit} onClick={() => void submit()}>
            {loading ? t('common.loading') : t('appointments.reschedule')}
          </Button>
        </CardFooter>
      </Card>
    </Modal>
  );
}

function StatTile({ icon, label, value, tone }: { icon: typeof CalendarCheck; label: string; value: number; tone: string }): JSX.Element {
  const Icon = icon;
  return (
    <div className="bg-card flex items-center gap-3 rounded-lg border p-3">
      <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', tone)} aria-hidden>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-xl leading-none font-semibold tabular-nums">{value}</p>
        <p className="text-muted-foreground mt-1 truncate text-xs">{label}</p>
      </div>
    </div>
  );
}

export function AppointmentsPage(): JSX.Element {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayStr());
  const [vetFilter, setVetFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showBook, setShowBook] = useState(false);
  const [reschedule, setReschedule] = useState<any>(null);
  const [confirmTarget, setConfirmTarget] = useState<any>(null);
  const [cancelTarget, setCancelTarget] = useState<any>(null);
  const [cancelling, setCancelling] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const branch_id = useBranchScope();

  // Keeps the "in progress right now" highlight honest without a refetch.
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(tick);
  }, []);

  const win = useMemo(() => dayWindowIso(date), [date]);
  const { data, loading, refetch } = useQuery(APPOINTMENTS_QUERY, {
    variables: { from: win.from, to: win.to, veterinarian_id: vetFilter || null, status: statusFilter || null, branch_id },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  });
  // Pending customer requests can be for any future date — not scoped to the day-list's
  // currently-viewed date, so this is a separate, always-visible, date-unbounded query.
  const { data: pendingData, refetch: refetchPending } = useQuery(APPOINTMENTS_QUERY, {
    variables: { from: null, to: null, veterinarian_id: null, status: 'requested', branch_id },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  });
  const pending: any[] = pendingData?.appointments ?? [];
  const { data: vetData } = useQuery(VETERINARIAN_USERS_QUERY, { fetchPolicy: 'cache-first' });
  const { data: roomData } = useQuery(APPT_CONSULTING_ROOMS_QUERY, { fetchPolicy: 'cache-first' });
  const appts: any[] = data?.appointments ?? [];
  const vets: any[] = vetData?.veterinarianUsers ?? [];
  const rooms: any[] = roomData?.consultingRooms?.items ?? [];
  const vetName = (id: string): string => vets.find((v) => v.id === id)?.full_name ?? '—';
  const vetPhoto = (id: string): string | null => vets.find((v) => v.id === id)?.photo_url ?? null;
  const roomName = (id: string | null): string | null => (id ? (rooms.find((r) => r.id === id)?.name ?? null) : null);

  const [checkIn] = useMutation(CHECKIN_APPOINTMENT_MUTATION);
  const [startA] = useMutation(START_APPOINTMENT_MUTATION);
  const [completeA] = useMutation(COMPLETE_APPOINTMENT_MUTATION);
  const [noShow] = useMutation(NOSHOW_APPOINTMENT_MUTATION);
  const [cancelA] = useMutation(CANCEL_APPOINTMENT_MUTATION);

  const run = async (fn: () => Promise<unknown>, msg: string): Promise<void> => {
    try {
      await fn();
      await Promise.all([refetch(), refetchPending()]);
      showToast({ tone: 'success', title: msg });
    } catch (e) {
      showToast({ tone: 'error', title: errText(e, t) });
    }
  };
  const idv = (appointment_id: string): any => ({ variables: { appointment_id }, context: idempotencyContext() });

  const count = (...statuses: string[]): number => appts.filter((a) => statuses.includes(a.status)).length;
  const is_today = date === todayStr();

  /** The one obvious next step for a row; everything else lives in the "more" menu. */
  const primaryAction = (a: any): { label: string; onClick: () => void } | null => {
    switch (a.status) {
      case 'requested':
      case 'booked':
        return { label: t('appointments.action_confirm'), onClick: () => setConfirmTarget(a) };
      case 'confirmed':
        return { label: t('appointments.action_checkin'), onClick: () => void run(() => checkIn(idv(a.id)), t('appointments.toast_checked_in')) };
      case 'checked_in':
        return { label: t('appointments.action_start'), onClick: () => void run(() => startA(idv(a.id)), t('appointments.toast_started')) };
      case 'in_progress':
        return { label: t('appointments.action_complete'), onClick: () => void run(() => completeA(idv(a.id)), t('appointments.toast_completed')) };
      default:
        return null;
    }
  };

  const onConfirmCancel = async (): Promise<void> => {
    if (!cancelTarget) return;
    setCancelling(true);
    await run(
      () => cancelA({ variables: { input: { appointment_id: cancelTarget.id } }, context: idempotencyContext() }),
      t('appointments.toast_cancelled'),
    );
    setCancelling(false);
    setCancelTarget(null);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('appointments.title')}</h1>
          <p className="text-muted-foreground flex items-center gap-2 text-sm first-letter:uppercase">
            {longDate(date)}
            {is_today ? <Badge variant="default">{t('appointments.today')}</Badge> : null}
          </p>
        </div>
        <Button onClick={() => setShowBook(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          <span>{t('appointments.new')}</span>
        </Button>
      </header>

      {pending.length > 0 ? (
        <section className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" aria-label={t('appointments.pending_requests_title')}>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-200">
            <Hourglass className="h-4 w-4" aria-hidden />
            {t('appointments.pending_requests_title')}
            <Badge variant="warning">{pending.length}</Badge>
          </h2>
          <ul className="space-y-1.5">
            {pending.map((a) => (
              <li key={a.id} className="bg-card flex flex-wrap items-center justify-between gap-2 rounded-md border p-2.5 text-sm">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-x-2">
                    <span className="font-semibold">{a.pet_name ?? '—'}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {new Date(a.scheduled_start_at).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })} · {hm(a.scheduled_start_at)}
                    </span>
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {vetName(a.veterinarian_id)}
                    {a.chief_complaint ? ` · ${a.chief_complaint}` : ''}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => setConfirmTarget(a)}>{t('appointments.action_confirm')}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setCancelTarget(a)}>{t('appointments.action_cancel')}</Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <StatTile icon={CalendarClock} label={t('appointments.stat_total')} value={appts.length} tone="bg-primary/10 text-primary" />
        <StatTile icon={CalendarCheck} label={t('appointments.stat_upcoming')} value={count('requested', 'booked', 'confirmed')} tone="bg-blue-500/15 text-blue-700 dark:text-blue-300" />
        <StatTile icon={Stethoscope} label={t('appointments.stat_active')} value={count('checked_in', 'in_progress')} tone="bg-violet-500/15 text-violet-700 dark:text-violet-300" />
        <StatTile icon={CircleCheck} label={t('appointments.stat_done')} value={count('completed')} tone="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="bg-card inline-flex items-center rounded-md border">
          <Button size="sm" variant="ghost" onClick={() => setDate(shiftDay(date, -1))} aria-label={t('appointments.prev')}>
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          <Button size="sm" variant="ghost" disabled={is_today} onClick={() => setDate(todayStr())}>{t('appointments.today')}</Button>
          <Button size="sm" variant="ghost" onClick={() => setDate(shiftDay(date, 1))} aria-label={t('appointments.next')}>
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
        <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-auto" aria-label={t('appointments.start')} />
        <select className="border-input bg-background h-9 rounded-md border px-2 text-sm" value={vetFilter} onChange={(e) => setVetFilter(e.target.value)}>
          <option value="">{t('appointments.filter_vet')}</option>
          {vets.map((v) => <option key={v.id} value={v.id}>{v.full_name}</option>)}
        </select>
        <select className="border-input bg-background h-9 rounded-md border px-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">{t('appointments.filter_status')}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`appointments.status_${s}`)}</option>)}
        </select>
        {vetFilter || statusFilter ? (
          <Button size="sm" variant="ghost" onClick={() => { setVetFilter(''); setStatusFilter(''); }}>
            {t('appointments.clear_filters')}
          </Button>
        ) : null}
      </div>

      {appts.length === 0 && loading ? (
        <ul className="space-y-2" aria-busy="true">
          {[0, 1, 2].map((i) => <li key={i} className="bg-muted/60 h-20 animate-pulse rounded-lg" />)}
        </ul>
      ) : appts.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground flex flex-col items-center gap-2 py-12 text-center text-sm">
            <CalendarX className="h-9 w-9 opacity-50" aria-hidden />
            <p>{t('appointments.no_appts')}</p>
            <Button size="sm" variant="outline" onClick={() => setShowBook(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              <span>{t('appointments.new')}</span>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2">
          {appts.map((a) => {
            const style = STATUS_STYLE[a.status] ?? STATUS_STYLE.completed;
            const locked = ['completed', 'no_show', 'cancelled', 'rescheduled'].includes(a.status);
            const room = roomName(a.consulting_room_id);
            const happening = is_today && !locked && now >= new Date(a.scheduled_start_at).getTime() && now < new Date(a.scheduled_end_at).getTime();
            const primary = primaryAction(a);
            const can_move = a.status === 'booked' || a.status === 'confirmed';
            const can_cancel = ['requested', 'booked', 'confirmed', 'checked_in'].includes(a.status);
            return (
              <li
                key={a.id}
                className={cn(
                  'bg-card flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-l-4 p-3 transition-shadow hover:shadow-md',
                  style?.accent,
                  locked && 'opacity-70',
                  happening && 'ring-primary/40 ring-2',
                )}
              >
                <div className="w-20 shrink-0 text-center">
                  <p className="text-lg leading-tight font-semibold tabular-nums">{hm(a.scheduled_start_at)}</p>
                  <p className="text-muted-foreground text-xs tabular-nums">{hm(a.scheduled_end_at)} · {a.duration_minutes} {t('appointments.min_short')}</p>
                </div>

                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{a.pet_name ?? '—'}</span>
                    <StatusBadge status={a.status} />
                    {happening ? <Badge variant="default">{t('appointments.now')}</Badge> : null}
                    <Badge variant="outline">{t(`appointments.kind_${a.appointment_kind}`)}</Badge>
                  </div>
                  <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span className="inline-flex items-center gap-1.5">
                      <UserAvatar name={vetName(a.veterinarian_id)} photo_url={vetPhoto(a.veterinarian_id)} size={20} />
                      {vetName(a.veterinarian_id)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <DoorOpen className="h-3.5 w-3.5" aria-hidden />
                      {room ?? t('appointments.room_none')}
                    </span>
                    <span className="font-mono">{a.folio_number}</span>
                  </div>
                  {a.chief_complaint ? <p className="text-muted-foreground truncate text-xs italic">“{a.chief_complaint}”</p> : null}
                </div>

                {!locked ? (
                  <div className="flex items-center gap-1">
                    {primary ? <Button size="sm" onClick={primary.onClick}>{primary.label}</Button> : null}
                    {can_move || can_cancel ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" aria-label={t('appointments.more_actions')}>
                            <MoreHorizontal className="h-4 w-4" aria-hidden />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          {can_move ? (
                            <DropdownMenuItem onSelect={() => setReschedule(a)}>{t('appointments.action_reschedule')}</DropdownMenuItem>
                          ) : null}
                          {can_move ? (
                            <DropdownMenuItem onSelect={() => void run(() => noShow(idv(a.id)), t('appointments.toast_no_show'))}>
                              {t('appointments.action_noshow')}
                            </DropdownMenuItem>
                          ) : null}
                          {can_move && can_cancel ? <DropdownMenuSeparator /> : null}
                          {can_cancel ? (
                            <DropdownMenuItem destructive onSelect={() => setCancelTarget(a)}>
                              {t('appointments.action_cancel')}
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {showBook ? (
        <BookDialog
          rooms={rooms}
          vets={vets}
          initialStart={defaultStart(date)}
          onClose={() => setShowBook(false)}
          onBooked={() => { setShowBook(false); void refetch(); showToast({ tone: 'success', title: t('appointments.toast_booked') }); }}
        />
      ) : null}
      {reschedule ? (
        <RescheduleDialog
          appt={reschedule}
          onClose={() => setReschedule(null)}
          onDone={() => { setReschedule(null); void refetch(); showToast({ tone: 'success', title: t('appointments.toast_rescheduled') }); }}
        />
      ) : null}
      {confirmTarget ? (
        <ConfirmRequestDialog
          appt={confirmTarget}
          vets={vets}
          rooms={rooms}
          onClose={() => setConfirmTarget(null)}
          onConfirmed={() => {
            setConfirmTarget(null);
            void refetch();
            void refetchPending();
            showToast({ tone: 'success', title: t('appointments.toast_confirmed') });
          }}
        />
      ) : null}
      <ConfirmDialog
        open={cancelTarget !== null}
        destructive
        title={t('appointments.cancel_title')}
        description={`${cancelTarget?.pet_name ?? ''} · ${cancelTarget ? hm(cancelTarget.scheduled_start_at) : ''} — ${t('appointments.cancel_confirm')}`}
        confirm_label={t('appointments.cancel_action')}
        cancel_label={t('appointments.cancel_back')}
        loading={cancelling}
        loading_label={t('common.loading')}
        on_confirm={onConfirmCancel}
        on_cancel={() => setCancelTarget(null)}
      />
    </div>
  );
}
